import type { BillingPackageRecord, Phase4PaddleEnvironment, Phase4PaymentMode } from "./types";

export const PAID_QUOTA_VALIDITY_DAYS = 90 as const;
export const PAID_QUOTA_VALIDITY_MS = PAID_QUOTA_VALIDITY_DAYS * 24 * 60 * 60 * 1000;
export const PUBLIC_STARTER_PACK = {
  quotaUnits: 30,
  amountMinor: 2999,
  currency: "USD",
  priceDisplay: "$29.99 USD",
  validityDays: PAID_QUOTA_VALIDITY_DAYS,
  purchaseType: "one_time",
} as const;

export type Phase4StarterPackEnvConfig = {
  paddlePriceId: string | null;
  quotaUnits: number | null;
  priceDisplay: string | null;
  amountMinor: number | null;
  currency: string | null;
  validityDays: number | null;
  perAccountPurchaseCap: number | null;
};

export type Phase4PaymentConfig = {
  mode: Phase4PaymentMode;
  paddleEnvironment: Phase4PaddleEnvironment;
  paddleCheckoutUrl: string | null;
  paddleClientToken: string | null;
  purchasesPaused: boolean;
  disclosureVersions: Record<string, string>;
  starterPack: Phase4StarterPackEnvConfig;
};

export const defaultPhase4PaymentConfig: Phase4PaymentConfig = {
  mode: "disabled",
  paddleEnvironment: "sandbox",
  paddleCheckoutUrl: null,
  paddleClientToken: null,
  purchasesPaused: true,
  disclosureVersions: {
    phase4PaymentTerms: "not_configured",
    phase4RefundPolicy: "not_configured",
  },
  starterPack: {
    paddlePriceId: null,
    quotaUnits: null,
    priceDisplay: null,
    amountMinor: null,
    currency: null,
    validityDays: null,
    perAccountPurchaseCap: null,
  },
};

export type Phase4PaymentEnvSource = {
  MULTIRESUME_PAYMENT_MODE?: string;
  MULTIRESUME_PAYMENT_PROVIDER?: string;
  /** @deprecated Use MULTIRESUME_PAYMENT_MODE. */
  MULTIRESUME_PHASE4_PAYMENT_MODE?: string;
  MULTIRESUME_PADDLE_ENVIRONMENT?: string;
  MULTIRESUME_PADDLE_CHECKOUT_URL?: string;
  MULTIRESUME_PADDLE_CLIENT_TOKEN?: string;
  FLAG_DISABLE_MULTIRESUME_PURCHASES?: string;
  MULTIRESUME_PADDLE_API_KEY?: string;
  MULTIRESUME_PADDLE_WEBHOOK_SECRET?: string;
  MULTIRESUME_PADDLE_STARTER_PACK_PRICE_ID?: string;
  MULTIRESUME_STARTER_PACK_QUOTA_UNITS?: string;
  MULTIRESUME_STARTER_PACK_PRICE_DISPLAY?: string;
  MULTIRESUME_STARTER_PACK_AMOUNT_MINOR?: string;
  MULTIRESUME_STARTER_PACK_CURRENCY?: string;
  MULTIRESUME_STARTER_PACK_VALIDITY_DAYS?: string;
  MULTIRESUME_STARTER_PACK_PER_ACCOUNT_CAP?: string;
  MULTIRESUME_PHASE4_PAYMENT_TERMS_VERSION?: string;
  MULTIRESUME_REFUND_POLICY_VERSION?: string;
};

function nonEmpty(value: string | undefined) {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function parseNonNegativeInteger(value: string | undefined) {
  const text = nonEmpty(value);
  if (!text) return null;
  if (!/^\d+$/.test(text)) return null;

  const parsed = Number(text);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

function parseHttpUrl(value: string | undefined) {
  const text = nonEmpty(value);
  if (!text) return null;

  try {
    const parsed = new URL(text);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
    return text;
  } catch {
    return null;
  }
}

function parsePaddleClientToken(value: string | undefined, environment: Phase4PaddleEnvironment) {
  const text = nonEmpty(value);
  if (!text) return null;
  if (environment === "sandbox") return text.startsWith("test_") ? text : null;
  return text.startsWith("live_") ? text : null;
}

function resolveEffectivePaymentMode(input: Phase4PaymentEnvSource): Phase4PaymentMode {
  const canonicalMode = nonEmpty(input.MULTIRESUME_PAYMENT_MODE);
  const requestedMode = canonicalMode ?? nonEmpty(input.MULTIRESUME_PHASE4_PAYMENT_MODE);
  const requestedPaddleEnvironment = input.MULTIRESUME_PADDLE_ENVIRONMENT;

  if (input.MULTIRESUME_PAYMENT_PROVIDER === "waffo_pancake") {
    if (requestedMode === "test") return "sandbox";
    if (requestedMode === "live") return "live";
    return "disabled";
  }

  if (requestedMode === "sandbox" && requestedPaddleEnvironment === "sandbox") return "sandbox";
  if (requestedMode === "live" && requestedPaddleEnvironment === "live") return "live";
  return "disabled";
}

function getProcessPaymentEnv(): Phase4PaymentEnvSource {
  return {
    MULTIRESUME_PAYMENT_MODE: process.env.MULTIRESUME_PAYMENT_MODE,
    MULTIRESUME_PAYMENT_PROVIDER: process.env.MULTIRESUME_PAYMENT_PROVIDER,
    MULTIRESUME_PHASE4_PAYMENT_MODE: process.env.MULTIRESUME_PHASE4_PAYMENT_MODE,
    MULTIRESUME_PADDLE_ENVIRONMENT: process.env.MULTIRESUME_PADDLE_ENVIRONMENT,
    MULTIRESUME_PADDLE_CHECKOUT_URL: process.env.MULTIRESUME_PADDLE_CHECKOUT_URL,
    MULTIRESUME_PADDLE_CLIENT_TOKEN: process.env.MULTIRESUME_PADDLE_CLIENT_TOKEN,
    FLAG_DISABLE_MULTIRESUME_PURCHASES: process.env.FLAG_DISABLE_MULTIRESUME_PURCHASES,
    MULTIRESUME_PADDLE_API_KEY: process.env.MULTIRESUME_PADDLE_API_KEY,
    MULTIRESUME_PADDLE_WEBHOOK_SECRET: process.env.MULTIRESUME_PADDLE_WEBHOOK_SECRET,
    MULTIRESUME_PADDLE_STARTER_PACK_PRICE_ID: process.env.MULTIRESUME_PADDLE_STARTER_PACK_PRICE_ID,
    MULTIRESUME_STARTER_PACK_QUOTA_UNITS: process.env.MULTIRESUME_STARTER_PACK_QUOTA_UNITS,
    MULTIRESUME_STARTER_PACK_PRICE_DISPLAY: process.env.MULTIRESUME_STARTER_PACK_PRICE_DISPLAY,
    MULTIRESUME_STARTER_PACK_AMOUNT_MINOR: process.env.MULTIRESUME_STARTER_PACK_AMOUNT_MINOR,
    MULTIRESUME_STARTER_PACK_CURRENCY: process.env.MULTIRESUME_STARTER_PACK_CURRENCY,
    MULTIRESUME_STARTER_PACK_VALIDITY_DAYS: process.env.MULTIRESUME_STARTER_PACK_VALIDITY_DAYS,
    MULTIRESUME_STARTER_PACK_PER_ACCOUNT_CAP: process.env.MULTIRESUME_STARTER_PACK_PER_ACCOUNT_CAP,
    MULTIRESUME_PHASE4_PAYMENT_TERMS_VERSION: process.env.MULTIRESUME_PHASE4_PAYMENT_TERMS_VERSION,
    MULTIRESUME_REFUND_POLICY_VERSION: process.env.MULTIRESUME_REFUND_POLICY_VERSION,
  };
}

export function resolvePhase4PaymentConfigFromEnv(
  input: Phase4PaymentEnvSource = getProcessPaymentEnv(),
): Phase4PaymentConfig {
  const mode = resolveEffectivePaymentMode(input);
  const paddleEnvironment = mode === "live" ? "live" : "sandbox";

  return {
    ...defaultPhase4PaymentConfig,
    mode,
    paddleEnvironment,
    paddleCheckoutUrl: parseHttpUrl(input.MULTIRESUME_PADDLE_CHECKOUT_URL),
    paddleClientToken:
      mode === "sandbox" || mode === "live"
        ? parsePaddleClientToken(input.MULTIRESUME_PADDLE_CLIENT_TOKEN, paddleEnvironment)
        : null,
    purchasesPaused: input.FLAG_DISABLE_MULTIRESUME_PURCHASES !== "false",
    disclosureVersions: {
      phase4PaymentTerms: nonEmpty(input.MULTIRESUME_PHASE4_PAYMENT_TERMS_VERSION) ?? "not_configured",
      phase4RefundPolicy: nonEmpty(input.MULTIRESUME_REFUND_POLICY_VERSION) ?? "not_configured",
    },
    starterPack: {
      paddlePriceId: nonEmpty(input.MULTIRESUME_PADDLE_STARTER_PACK_PRICE_ID),
      quotaUnits: parseNonNegativeInteger(input.MULTIRESUME_STARTER_PACK_QUOTA_UNITS),
      priceDisplay: nonEmpty(input.MULTIRESUME_STARTER_PACK_PRICE_DISPLAY),
      amountMinor: parseNonNegativeInteger(input.MULTIRESUME_STARTER_PACK_AMOUNT_MINOR),
      currency: nonEmpty(input.MULTIRESUME_STARTER_PACK_CURRENCY),
      validityDays: parseNonNegativeInteger(input.MULTIRESUME_STARTER_PACK_VALIDITY_DAYS),
      perAccountPurchaseCap: parseNonNegativeInteger(input.MULTIRESUME_STARTER_PACK_PER_ACCOUNT_CAP),
    },
  };
}

export function resolvePhase4PaddleClientConfigFromEnv(input: Phase4PaymentEnvSource = getProcessPaymentEnv()) {
  const config = resolvePhase4PaymentConfigFromEnv(input);

  return {
    mode: config.mode,
    paddleEnvironment: config.paddleEnvironment,
    paddleClientToken: config.mode === "sandbox" || config.mode === "live" ? config.paddleClientToken : null,
  };
}

export function resolvePhase4PaymentSecretsFromEnv(input: Phase4PaymentEnvSource = getProcessPaymentEnv()) {
  const config = resolvePhase4PaymentConfigFromEnv(input);
  if (config.mode !== "sandbox" && config.mode !== "live") {
    return {
      paddleApiKey: null,
      paddleWebhookSecret: null,
    };
  }

  return {
    paddleApiKey: nonEmpty(input.MULTIRESUME_PADDLE_API_KEY),
    paddleWebhookSecret: nonEmpty(input.MULTIRESUME_PADDLE_WEBHOOK_SECRET),
  };
}

export function getDefaultPhase4PaymentConfig(): Phase4PaymentConfig {
  return resolvePhase4PaymentConfigFromEnv();
}

export function getPhase4PaddleApiKey() {
  return resolvePhase4PaymentSecretsFromEnv().paddleApiKey;
}

export function getPhase4PaddleWebhookSecret() {
  return resolvePhase4PaymentSecretsFromEnv().paddleWebhookSecret;
}

export function getPhase4PaddleCheckoutUrl() {
  return resolvePhase4PaymentConfigFromEnv().paddleCheckoutUrl;
}

export function getPhase4PaddleClientConfig() {
  return resolvePhase4PaddleClientConfigFromEnv();
}

export function snapshotBillingPackage(pkg: BillingPackageRecord) {
  return {
    packageId: pkg.packageId,
    version: pkg.version,
    displayName: pkg.displayName,
    purchaseType: pkg.purchaseType,
    quotaUnits: pkg.quotaUnits,
    validityDays: pkg.validityDays ?? null,
    currency: pkg.currency,
    amountMinor: pkg.amountMinor ?? null,
    priceDisplay: pkg.priceDisplay,
    paddleEnvironment: pkg.paddleEnvironment,
    paddlePriceId: pkg.paddlePriceId ?? null,
    perAccountPurchaseCap: pkg.perAccountPurchaseCap ?? null,
  };
}
